import { ReactElement, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getLabelFallback, convertNumber } from '@utils/utils';
import * as actions from "@redux-actions/";
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

import PageTitle from '@components/PageTitle';
import CreditCardForm from '@components/CreditCardForm';
import PaymentType from '@components/PaymentType';
import PayPalCard from '@components/PayPalCard';
import AffirmCard from '@components/AffirmCard';
import AfterPayCard from '@components/AfterPayCard';
import Image from '@components/Image';
import SimpleElementSkeleton from '@components/skeletons/SimpleElementSkeleton';
import KlarnaPayCard from '@components/KlarnaPayCard';
import ApplePayCard from '@components/ApplePayCard'

import ArrowLeft from '@images/svg-icons/arrow_left.svg';
import Visa from '@images/svg-icons/visa.svg';
import Mastercard from '@images/svg-icons/mastercard.svg';
import Discover from '@images/svg-icons/discover.svg';
import Amex from '@images/svg-icons/amex.svg';
import PayPal2 from '@images/svg-icons/paypal2.svg';
import Affirm from '@images/svg-icons/affirm.svg';
import KlarnaImg from '@images/svg-icons/klarna.svg';
import ApplePay from '@images/svg-icons/applepay.svg';


type PaymentStepProps = {
    handleBack: () => void;
}

const PaymentStep = (props: PaymentStepProps) : ReactElement => {
    const {
        handleBack
    } = props;

    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    
    const cart = useSelector(state => state.cart.cart);
    const isMobile = useSelector(state => state.cart.isMobile);
    const hostname = useSelector(state => state.cart.config.hostname);
    const config = useSelector(state => state.cart.config);
    const address = useSelector(state => state.cart.address);
    const isAddressLoaded = useSelector(state => state.cart.isAddressLoaded);

    const [payment, setPayment] = useState(null);
    const [clientToken, setClientToken] = useState(null);
    const [requestId, setRequestId] = useState(null);
    const [errorKlarna, setErrorKlarna] = useState(null);
    const [showKlarnaLoader, setShowKlarnaLoader] = useState(true);
    const [klarnaPlaceholder, setShowKlarnaPlaceholder] = useState(true);
    const [controlCalls, setControlCalls] = useState(true);
    const [showApplePay, setShowApplePay] = useState(false);
    const label = getLabelFallback('PAYPAL_TEXT_LABEL_PART1')
        .replace('{SITE_NAME}', getLabelFallback('SITE_NAME'));

    useEffect(() => {
        if(!!cart && !!cart.paymentInstruction && cart.paymentInstruction.length > 0 ) {
            setPayment(cart.paymentInstruction[0].payMethodId.toLowerCase());
            dispatch(actions.getAddress(config.mockRequests, hostname, cart.orderId));
        } else {
            setPayment(null);
        }
    }, [cart]);

    useEffect(() => {
        var isPaypalExpressCheckout=(!!cart && !!cart.paymentInstruction && cart.paymentInstruction.length > 0 && cart.paymentInstruction[0].payMethodId === 'PayPal');
        if(!!cart && !isPaypalExpressCheckout && !!config && !!address && isAddressLoaded === true && controlCalls === true && config.klarna.enabled) {
            setControlCalls(false);
            console.log('>>> Klarna forward url', address)
            axios({
                url: config.hostname + config.klarna.forwardUrl,
                method: 'POST',
                data: {
                    orderId: cart.orderId,
                    storeId: config.storeId,
                    catalogId: config.catalogId,
                    shippingAddressId: !!address.billingAddressId ? address.billingAddressId : address.deliveryAddressId,
                    billingAddressId: address.deliveryAddressId
                }
            }).then(res => {
                console.log('>>> Klarna initialize', res)
                function initializeKlarna(count, client_token){
                    Klarna.Credit.init({client_token: client_token});
                    if (count < 2){
                        count++;
                        setTimeout(initializeKlarna.bind(null, count, client_token), 3000);
                    }
                }
                setClientToken(res.data.klarna_token);
                setRequestId(res.data.klarna_id);
            
                const client_token = res.data.klarna_token;
                const count = 0;
                initializeKlarna(count, client_token);
            }).catch(err => {
                console.log('>>> Klarna forward url error', err)
                if (err.errorMessage) {
                    setErrorKlarna(err.errorMessage);
                } else {
                    if (err.errorMessageKey) {
                        setErrorKlarna(err.errorMessageKey);
                    }
                }
                const obj = {
                    id: 'Event',
                    Events_Error: '1',
                    Error_Source: "Server",
                    Error_Code: "Checkout - Shipping",
                    Error_Details: 'Error klarna checkout',
                    Error_Message: 'Error klarna checkout'
                }
                window.tealium_data2track.push(obj);
            })
        } 
    }, [config, cart, address, isAddressLoaded]);

    useEffect(() => {
        if (searchParams.get('klarnaError') === 'true') {
            setErrorKlarna(getLabelFallback('KLARNA_WIDGET_ERROR'));
        }
        console.log(config.applepay.merchantIdentifier);
        if (window.ApplePaySession) {
            //alert("Apple Pay supported"); 
            if (config.applepay.merchantIdentifier != null) {
                // alert(config.applepay.merchantIdentifier)
                var promise = ApplePaySession.canMakePaymentsWithActiveCard(config.applepay.merchantIdentifier);
                promise.then(function (canMakePayments) {
                    // alert("canMakePayments for " + config.applepay.merchantIdentifier + " ? " + canMakePayments)
                    if (canMakePayments) {
                        setShowApplePay(true);
                    }
                }, function (reject) {
                    console.log("canMakePaymentsWithActiveCard promise rejected");
                });
            }
        } else {
            console.log("Apple Pay non supportata")
        }
    }, []);

    const klarnaPaymentChoosed = (e) => {
        console.log('>>> Klarna payment choosed', address);
        axios({
            url: config.hostname + config.klarna.updateSessionUrl,
            method: 'POST',
            data: {
                orderId: cart.orderId,
                storeId: config.storeId,
                catalogId: config.catalogId,
                shippingAddressId: !!address.billingAddressId ? address.billingAddressId : address.deliveryAddressId,
                billingAddressId: address.deliveryAddressId,
                requestId: requestId,
                klarnaToken: clientToken,
                updateLanguage: "EN_US"
            }
        }).then(res => {
            console.log('>>> Klarna update session ok:',res);
            if (res.data.klarnaError == 'true') {
                console.log('>>> Klarna error');
                /* @ts-expect-error */
                window.location = decodeURIComponent(res.data.redirecturl);
            } else {
                console.log('>>> Klarna ok, try to load');
                try {
                    setShowKlarnaLoader(false);
                    Klarna.Credit.load({
                        container: '#klarna-payments-container'
                    }, function (resk) {
                        if (resk["show_form"] == false) {
                            /* @ts-expect-error */
                            window.location = decodeURIComponent(res.data.redirecturl);
                        }
                    })
                } catch (e) {
                    console.log('>>> Klarna fail to load', e);
                    /* @ts-expect-error */
                    window.location = decodeURIComponent(config.klarna.redirectUrl);
                }
            }
        }).catch(err => {
            console.log('>>>> Klarna update session error', err);
            setShowKlarnaLoader(false);
            //command priority
            //$("#WC_commandPriorityKlarnaUpdate").val("false");
            if (err[0].errorMessage) {
                setErrorKlarna(err[0].errorMessage);
            } else {
                if (err[0].errorMessageKey) {
                    setErrorKlarna(err[0].errorMessageKey);
                }
            }
            const obj = {
                id: 'Event',
                Events_Error: '1',
                Error_Source: "Server",
                Error_Code: "Checkout - Shipping",
                Error_Details: 'Error klarna checkout',
                Error_Message: 'Error klarna checkout'
            }
            window.tealium_data2track.push(obj);
            /* @ts-expect-error */
            window.location = decodeURIComponent(res.data.redirecturl);
        });
        
        setPayment(e);
    }

    return (
        <div className="payment-step">
            {isMobile === false && <button className="back-link payment-step-back-link" onClick={handleBack}><ArrowLeft /> {getLabelFallback('HEADER_BACK_LABEL')}</button>}
            <PageTitle label={ getLabelFallback('PAYMENT_PAGE_TITLE') } />
            {
                            <span class="privacy-policy" dangerouslySetInnerHTML={{__html: label + ' ' + getLabelFallback('PAYPAL_TEXT_LABEL_PART2')
                                .replace('{TERM_AND_CONDITION_PAYPAL}', '<a class="primary-button" target="_blank" aria-label="'+getLabelFallback('TERM_AND_CONDITION_PAYPAL')+'" href="'+ config.linkTermCondition +'">'+ getLabelFallback('TERM_AND_CONDITION_PAYPAL')+'</a>')
                                .replace('{PRIVACY_POLICY_PAYPAL}', '<a class="primary-button" target="_blank" aria-label="'+getLabelFallback('PRIVACY_POLICY_PAYPAL')+'" href="'+ config.linkPrivacyPolicy +'">'+ getLabelFallback('PRIVACY_POLICY_PAYPAL')+'</a>')}}>
                            </span>
                        }
            <div className="payment-step-total">
                {getLabelFallback('TOTAL_PRICE_LABEL')}:
                <span>
                    {!!cart && getLabelFallback('CURRENCY_LABEL')}
                    {!!cart ? convertNumber(cart.grandTotal) : <SimpleElementSkeleton width={'100px'} height={'24px'} />}
                </span>
            </div>
            <div className="payment-step-content">
                <PaymentType 
                    id={'credit_card_button'}
                    checked={payment === 'credit-card'}
                    value={'credit-card'}
                    label={getLabelFallback('CREDIT_CARD_PAYMENT_TITLE')}
                    onChange={(e) => {setPayment(e.currentTarget.value)}}
                    onClick={e => {setPayment(e)}}
                    images={
                        [
                            <Amex />, 
                            <Visa />, 
                            <Mastercard />, 
                            <Discover />
                        ]}
                >
                    <CreditCardForm />
                </PaymentType>
                        
                <PaymentType 
                    id={'paypal_radio_button'}
                    checked={payment === 'paypal'}
                    value={'paypal'}
                    label={getLabelFallback('PAYPAL_PAYMENT_LABEL')}
                    onChange={(e) => {setPayment(e.currentTarget.value)}}
                    onClick={e => {setPayment(e)}}
                    images={ <PayPal2 />}
                >
                    <PayPalCard />
                </PaymentType>
                        
                
                {showApplePay && config.applepay.enabled === true && <PaymentType
                    id={'apple_pay_button'}
                    checked={payment === 'apple-pay'}
                    value={'apple-pay'}
                    label={getLabelFallback('APPLE_PAYMENT_LABEL')}
                    onChange={(e) => setPayment(e.currentTarget.value)}
                    onClick={e => {setPayment(e)}}
                    images={<ApplePay />}
                >
                    <ApplePayCard expressCheckout={false}/>
                </PaymentType>}
               
                <PaymentType
                    id={'affirm_button'}
                    checked={payment === 'affirm'}
                    value={'affirm'}
                    label={getLabelFallback('AFFIRM_PAYMENT_LABEL')}
                    onChange={(e) => setPayment(e.currentTarget.value)}
                    onClick={e => {setPayment(e)}}
                    images={ <Affirm />}
                    classes={!!cart && parseFloat(cart.grandTotal) < parseFloat(config.affirm.totalThresholdVal) ? 'hide' : ''}
                >
                    <AffirmCard />
                </PaymentType>
                
                {!!config.klarna && config.klarna.enabled === true && !!address && isAddressLoaded === true &&
                    <>
                        <PaymentType
                            id={'klarna_pay_button'}
                            checked={payment === 'klarna-pay'}
                            value={'klarna-pay'}
                            label={getLabelFallback('KLARNA_PAYMENT_LABEL')}
                            onChange={(e) => setPayment(e.currentTarget.value)}
                            onClick={e => {klarnaPaymentChoosed(e)}}
                            images={ <KlarnaImg />}
                            classes = {!!cart && parseFloat(cart.grandTotal) > 0 ? '' : 'hide' }
                        >
                            <KlarnaPayCard loader={showKlarnaLoader}/>
                        </PaymentType>
                        {!errorKlarna &&
                            <div className="klarna-error">
                                {errorKlarna}
                            </div>
                        }
                    </>
                }

                {/*<PaymentType
                    id={'after_pay_button'}
                    checked={payment === 'after-pay'}
                    value={'after-pay'}
                    label={getLabelFallback('AFTERPAY_PAYMENT_LABEL')}
                    onChange={(e) => setPayment(e.currentTarget.value)}
                    images={ <AfterPay />}
                >
                    <AfterPayCard />
                    </PaymentType>*/}
            </div>
            {!!config && !!config.klarna && !!config.klarna.script &&
                <Helmet>
                    <script type="text/javascript" src={config.klarna.script} async></script>
                </Helmet>
            }
        </div>        
    )
}

export default PaymentStep;
